import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="privacy-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-4">Privacy notice</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D332D] leading-[1.05] tracking-tight mb-6">
        Privacy, plainly.
      </h1>

      {/* Short version */}
      <div className="rounded-2xl bg-[#E9ECE8] border border-[#9EAD94]/40 p-6 md:p-8 mb-12">
        <div className="text-xs uppercase tracking-[0.15em] text-[#6B7F62] mb-2">The short version</div>
        <p className="font-serif text-xl md:text-2xl text-[#2D332D] leading-snug">
          What you save is yours. We don't read it. We don't share it. We don't sell it. Delete it whenever you like.
        </p>
      </div>

      <div className="prose-warm">
        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">Who runs this site</h2>
        <p>
          At The End of The Day is created and operated by Lauren Jordan (All Day VA), based in the United Kingdom.
          All Day VA is registered with the Information Commissioner's Office (ICO), registration number <span className="font-mono text-[#2D332D]">ZC042995</span>.
          For anything privacy-related you can write to{" "}
          <a href="mailto:lauren@alldayva.co.uk" className="text-[#6B7F62] underline hover:text-[#2D332D]">
            lauren@alldayva.co.uk
          </a>
          .
        </p>

        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">What we collect</h2>
        <p>Only what you give us on purpose:</p>
        <ul className="list-disc pl-6 space-y-1.5 text-[#576056]">
          <li><span className="text-[#2D332D] font-medium">Account details</span> — your name and email address, and a password that is one-way hashed (we can't read it).</li>
          <li><span className="text-[#2D332D] font-medium">Plan content</span> — everything you type into the Plan Builder: accounts, wishes, letters, etc. Held privately against your account.</li>
          <li><span className="text-[#2D332D] font-medium">Guide progress</span> — which checklist items you've ticked.</li>
          <li><span className="text-[#2D332D] font-medium">Chat questions</span> — your questions to the assistant and its replies, kept only so you can revisit the conversation.</li>
          <li><span className="text-[#2D332D] font-medium">Payment records</span> — if you buy lifetime access, a small record from Stripe (amount, date, reference). We never see or store your card details.</li>
        </ul>

        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">What we don't collect</h2>
        <ul className="list-disc pl-6 space-y-1.5 text-[#576056]">
          <li>No Google Analytics, Facebook pixel, or advertising trackers.</li>
          <li>No card numbers — Stripe handles all payment.</li>
          <li>No location data, no device fingerprinting.</li>
          <li>Nothing sold, ever, to any third party.</li>
        </ul>

        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">Who we share with</h2>
        <p>Only the essential services that run the site:</p>
        <ul className="list-disc pl-6 space-y-1.5 text-[#576056]">
          <li><span className="text-[#2D332D] font-medium">Stripe</span> — payments only. They see your card, we don't.</li>
          <li><span className="text-[#2D332D] font-medium">Our AI chat provider</span> — your chat questions are sent so the assistant can answer. Providers we use contractually agree not to use your data to train their models.</li>
          <li><span className="text-[#2D332D] font-medium">Hosting</span> — the servers that keep the site running.</li>
        </ul>
        <p>That's it. No advertisers, no data brokers, no marketing lists.</p>

        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">How long we keep it</h2>
        <p>
          For as long as you have an account. When you delete your account, everything — your Plan, your progress, your chats, your payment records — is permanently removed from our database within 30 days.
        </p>

        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">Your rights</h2>
        <p>Under UK GDPR you can, at any time and free of charge:</p>
        <ul className="list-disc pl-6 space-y-1.5 text-[#576056]">
          <li>See exactly what we hold about you</li>
          <li>Correct anything wrong</li>
          <li>Delete your account and everything in it</li>
          <li>Export your Plan as a PDF</li>
          <li>Object to any processing that upsets you</li>
          <li>Complain to the Information Commissioner's Office (ICO) if you feel we've fallen short</li>
        </ul>
        <p>
          Just email <a href="mailto:lauren@alldayva.co.uk" className="text-[#6B7F62] underline hover:text-[#2D332D]">lauren@alldayva.co.uk</a> — we reply within 7 days.
        </p>

        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">Cookies</h2>
        <p>
          We use a single cookie to keep you signed in. We don't use cookies for tracking, advertising, or analytics.
        </p>

        <h2 className="font-serif text-2xl text-[#2D332D] mt-10 mb-3">Changes to this notice</h2>
        <p>
          If we ever change anything material, you'll be told at the top of the page. Everyday polishing (a clearer word, a fixed typo) happens quietly.
        </p>

        <p className="text-sm text-[#8A9488] mt-10">Last updated: February 2026.</p>
      </div>

      <div className="mt-14 flex flex-wrap items-center gap-3 text-sm text-[#8A9488]">
        <Link to="/security" className="inline-flex items-center gap-1.5 hover:text-[#2D332D]" data-testid="privacy-to-security">
          See how your data is kept safe <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
